import styled from "styled-components";

import { Content } from "../../Subscription/pages/Features/styles";
import { MOBILE } from "./mobile";

/* The search screen: the green glow on top, as on the PLUS page (its hero, layout and benefits are reused) */
export const Page = styled.div`
  min-height: 100dvh;
  background:
    radial-gradient(60% 440px at 50% 0%, rgba(87, 242, 139, 0.14) 0%, rgba(87, 242, 139, 0) 70%),
    var(--color-neutral-darkness);

  &,
  & * {
    font-family: Inter, sans-serif;
  }
`;

/* The PLUS page's column, without the room it leaves for its fixed "Assinar" bar (there's none here; the app
   already leaves the bottom nav's room) */
export const SearchContent = styled(Content)`
  padding-bottom: 3rem;

  @media ${MOBILE} {
    padding-bottom: 1.5rem;
  }
`;

/* Phones have no sidebar (and its Domains subitems): the other two screens as links above the search */
export const MobileLinks = styled.nav`
  display: none;

  @media ${MOBILE} {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
    margin-bottom: 1.5rem;
  }
`;

/* The search, where the PLUS page has its plans: one card, with a few of the extensions sold under it */
export const SearchCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  width: 100%;
  max-width: 520px;
  padding: 1rem;
  margin-top: 1.75rem;
  text-align: left;
  background: rgba(38, 42, 52, 0.85);
  border: 1px solid var(--color-neutral-base);
  border-radius: 1rem;

  .tlds {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem 0.75rem;
    align-items: center;
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }

  /* Plain text, like the line it sits on */
  .tld {
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-neutral-white);
  }

  .soon {
    margin-left: auto;

    @media ${MOBILE} {
      display: none;
    }
  }
`;

/* The search's results, under the hero */
export const Results = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  /* The same divider as the benefits' (and the PLUS page's): fades out at both ends */
  &::before {
    width: 100%;
    height: 1px;
    margin: 2.5rem 0 1.25rem;
    content: "";
    background: linear-gradient(90deg, transparent, var(--color-neutral-base), transparent);
  }

  /* Apart from the name searched for */
  .suggestions {
    margin-top: 1.25rem;
  }
`;

export const SearchForm = styled.form`
  display: flex;
  gap: 0.5rem;

  .field {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    padding: 0 1rem;
    background: var(--color-neutral-darkness);
    border: 1px solid var(--color-neutral-base);
    border-radius: 0.875rem;
    transition: border-color 0.15s;

    &:focus-within {
      border-color: var(--color-brand-plus);
    }
  }

  .prefix {
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--color-neutral-light);
  }

  input {
    flex: 1;
    min-width: 0;
    height: 52px;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-neutral-white);
    background: transparent;
    outline: none;

    &::placeholder {
      font-weight: 500;
      color: var(--color-neutral-light);
    }
  }

  button[type="submit"] {
    display: flex;
    flex-shrink: 0;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    min-width: 7rem;
    padding: 0 1.25rem;
    font-size: 0.9375rem;
    font-weight: 700;
    color: var(--color-neutral-white);
    background: var(--color-brand-blue);
    border-radius: 0.875rem;

    &:disabled {
      opacity: 0.6;
    }
  }

  /* Phones: the button stays beside the field, just narrower */
  @media ${MOBILE} {
    button[type="submit"] {
      min-width: 0;
      padding: 0 1rem;
    }
  }
`;

/* The name searched for: the big answer */
export const ExactResult = styled.div<{ $available: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.125rem;
  background: var(--color-neutral-dark);
  border: 1px solid
    ${props =>
      props.$available ? "color-mix(in srgb, var(--color-brand-plus) 55%, transparent)" : "var(--color-neutral-base)"};
  border-radius: 1rem;

  /* The name and the button; the cycles under them */
  .top {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;

    @media ${MOBILE} {
      flex-direction: column;
      align-items: stretch;
    }
  }

  /* The name and its tags on one line (they wrap under it when narrow) */
  .title {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem 0.5rem;
    align-items: center;
    min-width: 0;
  }

  .name {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 0;
  }

  .domain {
    overflow: hidden;
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--color-neutral-white);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .status {
    display: flex;
    gap: 0.375rem;
    align-items: center;
    font-size: 0.8125rem;
    font-weight: 700;
    color: ${props => (props.$available ? "var(--color-brand-plus)" : "var(--color-neutral-150)")};
  }
`;

/* The cycles (1, 2, 5 years) as the PLUS page's plans: selectable cards, the saving called out */
export const Cycles = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;

  /* The radio on the left, what's picked centered in the rest */
  button {
    position: relative;
    display: flex;
    gap: 0.5rem;
    align-items: center;
    padding: 0.625rem 0.625rem 0.5rem;
    background: rgba(38, 42, 52, 0.5);
    border: 1px solid var(--color-neutral-base);
    border-radius: 0.75rem;
    transition:
      border-color 0.15s,
      background-color 0.15s;

    &:hover {
      border-color: var(--color-neutral-light);
    }

    /* Same 1px border when picked (a 2px one moved the content); the second pixel is an inset ring */
    &[aria-checked="true"] {
      background: rgba(38, 42, 52, 0.85);
      border-color: var(--color-brand-plus);
      box-shadow: inset 0 0 0 1px var(--color-brand-plus);
    }
  }

  /* The checkout's radio (Keepo, payment method) */
  .radio {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border: 2px solid var(--color-neutral-light);
    border-radius: 100%;
  }

  button[aria-checked="true"] .radio {
    border: 5px solid var(--color-brand-plus);
  }

  .info {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    min-width: 0;
    text-align: left;
  }

  s {
    font-size: 0.6875rem;
    color: var(--color-neutral-light);
  }

  b {
    font-size: 1.0625rem;
    font-weight: 800;
    line-height: 1.375rem;
    color: var(--color-neutral-white);
  }

  .years {
    font-size: 0.6875rem;
    color: var(--color-neutral-white);
  }

  /* The full price and the saving, on one line inside the card */
  .was {
    display: flex;
    gap: 0.25rem;
    align-items: baseline;
  }

  .off {
    font-size: 0.6875rem;
    font-weight: 800;
    color: var(--color-brand-plus);
  }

  /* Phones: the cards are narrow; a smaller radio on top, the content on the whole card */
  @media ${MOBILE} {
    gap: 0.5rem;

    /* The radio on top, the content under it with some room */
    button {
      flex-direction: column;
      gap: 0.5rem;
      align-items: flex-start;
      padding: 0.5rem 0.5rem 0.5rem;
    }

    .radio {
      width: 14px;
      height: 14px;
    }

    button[aria-checked="true"] .radio {
      border-width: 4px;
    }

    b {
      font-size: 0.9375rem;
      line-height: 1.25rem;
    }
  }
`;

/* The other options' price: the value, "for 1 year" under it and the renewal */
export const Price = styled.span`
  display: flex;
  gap: 0.125rem;
  align-items: baseline;
  white-space: nowrap;

  b {
    font-size: 1.0625rem;
    font-weight: 800;
    line-height: 1.375rem;
    color: var(--color-neutral-white);
  }

  small {
    font-size: 0.75rem;
    color: var(--color-neutral-150);
  }
`;

export const BuyButton = styled.button`
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-neutral-white);
  white-space: nowrap;
  background: var(--color-brand-blue);
  border-radius: 100px;

  &.secondary {
    color: var(--color-neutral-white);
    background: var(--color-neutral-base);
  }
`;

/* The other extensions: one row each */
export const Suggestions = styled.ul`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--color-neutral-dark);
  border-radius: 1rem;

  li {
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    padding: 0.875rem 1.25rem;

    & + li {
      border-top: 1px solid var(--color-neutral-base);
    }

    &.taken {
      opacity: 0.55;
    }
  }

  .domain {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem 0.5rem;
    align-items: center;
    min-width: 0;
    font-size: 0.9375rem;
    font-weight: 700;
    /* Room for g, j, p, y: with the "…" cut (overflow hidden), a line-height of 1 clipped their tails */
    line-height: 1.375rem;
    color: var(--color-neutral-white);

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .name {
      display: flex;
      min-width: 0;
    }

    .tld {
      flex-shrink: 0;
      overflow: visible;
    }
  }

  /* The button's height even without one ("Já tem dono"): every row the same height */
  .right {
    display: flex;
    flex-shrink: 0;
    gap: 1rem;
    align-items: center;
    min-height: 2.375rem;
  }

  /* Phones: one row, the name with its seal under it / the price / the button */
  @media ${MOBILE} {
    li {
      gap: 0.5rem;
      padding: 0.875rem 1rem;
    }

    .domain {
      flex: 1;
      flex-direction: column;
      flex-wrap: nowrap;
      gap: 0.25rem;
      align-items: flex-start;
      max-width: 100%;
    }

    /* The name and the seal never past the column: cut with "…" */
    .domain > span {
      max-width: 100%;
    }

    /* A tighter price and button, to leave the name more room */
    .right {
      gap: 0.5rem;

      b {
        font-size: 0.9375rem;
      }

      button {
        padding: 0.625rem 0.875rem;
        font-size: 0.8125rem;
      }
    }
  }
`;
